import React from 'react';
import OptImage from '../utils/OptImage';

const ProcedureModal = ({ isOpen, onClose, procedure }) => {
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  React.useEffect(() => {
    if (!isOpen || !procedure) return;
    
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, procedure]);

  if (!isOpen || !procedure) return null;

  const procedureDetails = {
    1: {
      fullDescription: "Our facial rejuvenation procedures combine the latest techniques with Dr. Deepika Sinha's expertise to restore youthful vitality to your face.",
      benefits: [
        "Natural-looking results that enhance your features",
        "Minimally invasive techniques with faster recovery",
        "Customized treatment plans for individual needs",
        "Long-lasting improvements in facial appearance"
      ],
      procedures: [
        "Hydrafacial",
        "Oxygeneo Therapy",
        "Microneedling with PRP",
        "Chemical Peels",
        "Dermabraision"
      ],
      duration: "1-4 hours depending on procedure",
      recovery: "1-2 weeks for most procedures"
    },
    2: {
      fullDescription: "Transform your body silhouette with our advanced body contouring procedures. Dr. Deepika Sinha specializes in liposuction, tummy tucks, and body lifts that help you achieve the shape you desire with natural-looking results.",
      benefits: [
        "Improved body proportions and silhouette",
        "Removal of stubborn fat deposits",
        "Enhanced self-confidence and body image",
        "Permanent results with proper maintenance"
      ],
      procedures: [
        "Gynacomastia Surgery",
        "Fat Grafting",
        "Liposuction and Smart Lipo",
        "Tummy Tuck (Abdominoplasty)",
        "Body Lift and Thigh Lift",
        "Arm Lift and Brazilian Butt Lift",
        "Mommy Makeover",
      ],
      duration: "2-5 hours depending on areas treated",
      recovery: "2-4 weeks for optimal healing"
    },
    4: {
      fullDescription: "Our breast enhancement procedures are designed to help you achieve your desired breast size and shape. Whether you're looking for augmentation, reduction, or reconstruction, Dr. Deepika Sinha provides personalized care with natural results.",
      benefits: [
        "Improved breast size and shape",
        "Enhanced body proportion and symmetry",
        "Boost in self-confidence and comfort",
        "Variety of implant options and techniques"
      ],
      procedures: [
        "Breast Augmentation with Implants",
        "Breast Reduction and Lift",
        "Breast Reconstruction",
        "Revision Breast Surgery"
      ],
      duration: "1-3 hours depending on procedure",
      recovery: "1-2 weeks with limited activity"
    },
    5: {
      fullDescription: "Experience the power of advanced laser technology for skin rejuvenation, hair removal, and various skin conditions. Our state-of-the-art US FDA approved laser treatments provide effective solutions with minimal downtime.",
      benefits: [
        "Non-invasive treatment options",
        "Minimal downtime and discomfort",
        "Effective for various skin concerns",
        "Long-lasting results with maintenance"
      ],
      procedures: [
        "Permanent Hair Removal",
        "Laser Skin Resurfacing",
        "Acne Scar Treatment",
        "Pigmentation and Sun Damage Treatment",
        "Carbon Facial",
        "Melasma Correction",
        "Vascular Lesion Treatment",
        "Tattoo Removal",
        "Dark spots, freckles, and birthmark removal"
      ],
      duration: "30 minutes - 2 hours per session",
      recovery: "Minimal downtime, immediate return to activities"
    },
    3: {
      fullDescription: "Restore your hairline and confidence with our advanced hair restoration techniques. Dr. Deepika Sinha offers both surgical and non-surgical solutions for male and female pattern baldness.",
      benefits: [
        "Natural-looking hair growth patterns",
        "Permanent solution to hair loss",
        "Improved hairline and density",
        "Boost in self-confidence and appearance"
      ],
      procedures: [
        "FUE Hair Transplant",
        "FUT Hair Transplant",
        "PRP Hair Treatment",
        "GFC Hair Therapy",
        "Scalp Micropigmentation"
      ],
      duration: "4-8 hours depending on grafts needed",
      recovery: "1-2 weeks for healing, 3-6 months for growth"
    },
    6: {
      fullDescription: "Turn back the clock with our comprehensive anti-aging treatments. From non-invasive options to surgical procedures, we offer solutions to help you maintain a youthful appearance at any age.",
      benefits: [
        "Reduction in fine lines and wrinkles",
        "Improved skin texture and tone",
        "Non-surgical and surgical options available",
        "Customized treatment plans for optimal results"
      ],
      procedures: [
        "Botox and Dermal Fillers",
        "Facelift, Brow Lift, and Neck Lift",
        "Thread Lifts and Mini Facelifts",
        "Skin Tightening Treatments"
      ],
      duration: "30 minutes - 3 hours depending on treatment",
      recovery: "Minimal to 1-2 weeks depending on procedure"
    },
    7: {
      fullDescription: "Enhance and define your facial features with our specialized facial contouring procedures. Dr. Deepika Sinha utilizes advanced techniques to help you achieve a balanced and harmonious facial structure.",
      benefits: [
        "Enhanced facial symmetry and proportions",
        "Minimally invasive techniques available",
        "Customized treatment plans for individual features",
        "Natural-looking results that enhance your beauty"
      ],
      procedures: [
        "Nose Job (Rhinoplasty)",
        "Lip Job & Fillers",
        "Ear Piercing",
        "Split Ear Lobe Repair (Lobuloplasty)",
        "Eyelid Surgery (Blepharoplasty)",
        "Microblading for Eyebrows",
        "Chin/Cheek/Jaw Reshaping",
      ],
      duration: "1-4 hours depending on procedure",
      recovery: "1-2 weeks for most procedures"
    },
    8: {
      fullDescription: "Our vitiligo and color correction treatments are designed to restore skin pigmentation and improve overall skin appearance. Dr. Deepika Sinha offers a range of effective solutions tailored to your needs.",
      benefits: [
        "Effective restoration of skin color",
        "Improved skin appearance and texture",
        "Customized treatment plans for best results",
        "Combination of medical and surgical options"
      ],
      duration: "30 minutes - 2 hours per session",
      recovery: "Minimal downtime, varies by treatment",
      procedures: [
        "Vitiligo Surgery",
        "IC Glow Drip",
        "Scar Correction and Removal",
        "Birthmark and Mole Removal",
      ]
    },
    9: {
      fullDescription: "Enhance your feminine confidence with our specialized female aesthetic surgeries. Dr. Deepika Sinha provides a range of procedures tailored to meet the unique needs of women.",
      benefits: [
        "Enhanced feminine confidence and comfort",
        "Improved vaginal tightness and aesthetics",
        "Customized treatment plans for individual needs",
        "Minimally invasive techniques with faster recovery"
      ],
      duration: "1-3 hours depending on procedure",
      recovery: "1-2 weeks with limited activity",
      procedures: [
        "Varicose Veins Treatment",
        "Vaginal Tightening (Vaginoplasty)",
        "Labiaplasty",
        "Hymenoplasty",
        "Vaginoplasty",
        "Monsplasty",
      ]
    }
  };

  const details = procedureDetails[procedure.id] || {};

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="procedure-modal-content">
        <div className="procedure-modal-header">
          <div className="procedure-header-content">
            <div className="procedure-icon-large">{procedure.icon}</div>
            <div>
              <h2>{procedure.title}</h2>
              <p>{procedure.description}</p>
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="procedure-modal-body">
          <div className="procedure-image-section">
            <OptImage src={procedure.image} alt={procedure.title} className="procedure-large-image" />
          </div>
          
          <div className="procedure-details-section">
            <div className="procedure-description">
              <h3>About This Procedure</h3>
              <p>{details.fullDescription}</p>
            </div>

            <div className="procedure-info-grid">
              <div className="procedure-benefits">
                <h4>Key Benefits</h4>
                <ul>
                  {details.benefits?.map((benefit, index) => (
                    <li key={index}>{benefit}</li>
                  ))}
                </ul>
              </div>

              <div className="procedure-types">
                <h4>Available Procedures</h4>
                <ul>
                  {details.procedures?.map((proc, index) => (
                    <li key={index}>{proc}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="procedure-timeline">
              <div className="timeline-item">
                <h4>⏱️ Duration</h4>
                <p>{details.duration}</p>
              </div>
              <div className="timeline-item">
                <h4>🔄 Recovery Time</h4>
                <p>{details.recovery}</p>
              </div>
            </div>

            <div className="procedure-actions">
              <button 
                className="consultation-btn"
                onClick={() => window.location.href = 'tel:+91-9654495307'}
              >
                📞 Schedule Consultation
              </button>
              <button 
                className="info-btn"
                onClick={() => window.open('https://wa.me/919654495307', '_blank')}
              >
                💬 WhatsApp Info
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcedureModal;